import { Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useAuth } from "./auth/AuthContext";
import { Navbar } from "./components/Navbar";
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
import { WatchPage } from "./pages/WatchPage";
import { UploadPage } from "./pages/UploadPage";
import { MyVideosPage } from "./pages/MyVideosPage";
import { UsersPage } from "./pages/UsersPage";
import { ProfilePage } from "./pages/ProfilePage";
import { useT } from "./i18n";
import { useTheme } from "./theme";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-brand-muted">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-brand-dark">
      <Navbar />
      {children}
    </div>
  );
}

export function App() {
  const { user } = useAuth();
  const { setLocale } = useT();
  const { setPref } = useTheme();
  const synced = useRef(false);

  // On first login, sync locale + theme from the server profile (unless the
  // user has a local override already).
  useEffect(() => {
    if (!user || synced.current) return;
    synced.current = true;
    if (!localStorage.getItem("helix.locale")) setLocale(user.locale);
    if (!localStorage.getItem("helix.theme")) setPref(user.theme);
  }, [user, setLocale, setPref]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Shell>
              <HomePage />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/watch/:id"
        element={
          <RequireAuth>
            <Shell>
              <WatchPage />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/upload"
        element={
          <RequireAuth>
            <Shell>
              <UploadPage />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/my-videos"
        element={
          <RequireAuth>
            <Shell>
              <MyVideosPage />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAuth>
            <Shell>
              <UsersPage />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <Shell>
              <ProfilePage />
            </Shell>
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}